import React, { useState, useEffect, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { Drawer } from '../../../theme/components';
import { useAuth } from '../../../contexts/AuthContext';
import SaleHistoryTimeline from './SaleHistoryTimeline';
import AddTrackingModal from './AddTrackingModal';
import MarkAsPaidModal from './MarkAsPaidModal';
import SendEmailModal from './SendEmailModal';
import CreateTicketModal from './CreateTicketModal';
import { API_URL } from '../../../config/api';
import CurrencyFormatter from '../../../utils/CurrencyFormatter';

/**
 * SaleDetailsDrawer - Drawer completo de gestao da venda
 *
 * Usado na SalesManagementPage como tela principal de detalhes + acoes.
 * Inclui extrato financeiro, historico, e rodape fixo com botoes de acao.
 */
const SaleDetailsDrawer = ({
  show,
  onHide,
  saleId,
  onSaleUpdated
}) => {
  const { user: currentUser } = useAuth();
  const [sale, setSale] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commissionsLoading, setCommissionsLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [hasMoreHistory, setHasMoreHistory] = useState(true);

  // Estados dos modais de acao
  const [showAddTracking, setShowAddTracking] = useState(false);
  const [showMarkAsPaid, setShowMarkAsPaid] = useState(false);
  const [showSendEmail, setShowSendEmail] = useState(false);
  const [showCreateTicket, setShowCreateTicket] = useState(false);

  // Permissoes do usuario (mesma logica do SaleDetailsModal)
  const permissions = useMemo(() => {
    if (!sale || !currentUser) {
      return { canDoAll: false, canComment: false, canTrack: false };
    }

    const userId = currentUser.id;
    const isProducer = sale.producer_id === userId;
    const isCoprodutor = sale.is_coproducer === true;
    const isAffiliate = sale.affiliate_id === userId;
    const isManagerDirectSale = sale.is_manager === true && sale.affiliate_id === userId;
    const isSupplier = sale.is_supplier === true;
    const isLogisticsRep = sale.is_logistics_rep === true;
    const isManager = sale.is_manager === true;

    const canDoAll = isProducer || isCoprodutor || isAffiliate || isManagerDirectSale;
    const canComment = canDoAll || isSupplier || isLogisticsRep || isManager;
    const canTrack = isProducer || isCoprodutor || isManagerDirectSale || isLogisticsRep;

    return { canDoAll, canComment, canTrack };
  }, [sale, currentUser]);

  useEffect(() => {
    if (show && saleId) {
      loadSaleDetails();
      loadCommissions();
      loadSaleHistory(1, true);
    }
  }, [show, saleId]);

  const loadSaleDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/sales/${saleId}/details`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Erro ao carregar detalhes');

      const data = await response.json();
      setSale(data.data);
    } catch (err) {
      console.error('Erro ao carregar venda:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadCommissions = async () => {
    try {
      setCommissionsLoading(true);
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/sales/${saleId}/commissions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setCommissions(data.commissions || []);
      }
    } catch (err) {
      console.error('Erro ao carregar comissoes:', err);
    } finally {
      setCommissionsLoading(false);
    }
  };

  const loadSaleHistory = async (page = 1, reset = false) => {
    setHistoryLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(
        `${API_URL}/api/sales/${saleId}/history?page=${page}&limit=10`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      const responseData = await response.json();
      if (!response.ok) throw new Error(responseData.error || 'Erro ao carregar historico');

      const historyData = responseData.data || {};
      const newHistory = historyData.data || [];
      const pagination = historyData.pagination || { currentPage: 1, totalPages: 1 };

      if (reset) {
        setHistory(newHistory);
      } else {
        setHistory(prev => [...prev, ...newHistory]);
      }

      setHistoryPage(page);
      setHasMoreHistory(pagination.currentPage < pagination.totalPages);
    } catch (err) {
      console.error('Erro ao carregar historico:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleLoadMoreHistory = () => {
    if (!historyLoading && hasMoreHistory) {
      loadSaleHistory(historyPage + 1, false);
    }
  };

  const handleActionSuccess = () => {
    loadSaleDetails();
    loadSaleHistory(1, true);
    loadCommissions();
    if (onSaleUpdated) onSaleUpdated();
  };

  // Helpers
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const formatPrice = (priceInCents) => {
    return CurrencyFormatter.centsToFormatted(priceInCents);
  };

  const getPaymentStatusBadge = (status) => {
    const config = {
      'paid': { color: 'success', icon: 'solar:check-circle-bold', text: 'Pago' },
      'pending': { color: 'warning', icon: 'solar:clock-circle-bold', text: 'Pendente' },
      'processing': { color: 'primary', icon: 'solar:settings-bold', text: 'Processando' },
      'scheduled': { color: 'info', icon: 'solar:calendar-bold', text: 'Agendado' },
      'cancelled': { color: 'danger', icon: 'solar:close-circle-bold', text: 'Cancelado' },
      'refunded': { color: 'danger', icon: 'solar:restart-bold', text: 'Reembolsado' },
      'frustrated': { color: 'danger', icon: 'solar:close-circle-bold', text: 'Frustrado' }
    };

    const c = config[status] || config['pending'];
    return (
      <span className={`badge bg-${c.color} text-white d-flex align-items-center gap-1`}>
        <Icon icon={c.icon} className="text-xs" />
        {c.text}
      </span>
    );
  };

  const getPaymentMethodIcon = (method) => {
    const methods = {
      'credit_card': 'mdi:credit-card',
      'debit_card': 'mdi:credit-card-outline',
      'pix': 'mdi:qrcode',
      'boleto': 'mdi:file-document',
      'after_pay': 'mdi:truck-delivery'
    };
    return methods[method] || 'mdi:credit-card';
  };

  const getPaymentMethodText = (method) => {
    const methods = {
      'credit_card': 'Cartao de Credito',
      'debit_card': 'Cartao de Debito',
      'pix': 'PIX',
      'boleto': 'Boleto',
      'after_pay': 'Pagamento Apos Entrega'
    };
    return methods[method] || method;
  };

  const getRoleTypeBadge = (roleType) => {
    const config = {
      'platform': { color: 'bg-dark', text: 'Taxa Pagmus' },
      'supplier': { color: 'bg-secondary', text: 'Fornecedor' },
      'logistics_rep': { color: 'bg-secondary', text: 'Logística' },
      'affiliate': { color: 'bg-info', text: 'Afiliado' },
      'manager': { color: 'bg-primary', text: 'Gerente' },
      'coproducer': { color: 'bg-warning', text: 'Coprodutor' },
      'producer': { color: 'bg-success', text: 'Produtor' }
    };
    const key = (roleType || '').toLowerCase();
    const c = config[key] || { color: 'bg-secondary', text: roleType };
    return <span className={`badge ${c.color} text-white`}>{c.text}</span>;
  };

  const getAfterPayStatus = () => {
    if (!sale || !sale.is_afterpay) return null;

    const { payment_status, tracking_code, delivered_at, metadata } = sale;

    const getDaysSinceDelivery = (deliveredDate) => {
      if (!deliveredDate) return null;
      const now = new Date();
      const delivered = new Date(deliveredDate);
      return Math.ceil(Math.abs(now - delivered) / (1000 * 60 * 60 * 24));
    };

    let parsedMetadata = {};
    if (metadata) {
      try {
        parsedMetadata = typeof metadata === 'string' ? JSON.parse(metadata) : metadata;
      } catch (e) {
        parsedMetadata = {};
      }
    }

    const validationErrors = parsedMetadata.validation_errors;
    const trackingStatus = parsedMetadata.tracking_status;
    const attentionPriority = parseInt(parsedMetadata.attention_priority) || 0;

    if (validationErrors && Array.isArray(validationErrors) && validationErrors.length > 0) {
      return { status: 'Reportado', color: 'warning', icon: 'solar:danger-triangle-bold' };
    }
    if (!tracking_code) {
      return { status: 'Em Expedicao', color: 'info', icon: 'solar:box-bold' };
    }
    if (tracking_code && (!trackingStatus || (trackingStatus !== 'out_for_delivery' && trackingStatus !== 'available_for_pickup'))) {
      return { status: 'Enviado', color: 'primary', icon: 'solar:delivery-bold' };
    }
    if (trackingStatus === 'out_for_delivery') {
      return { status: 'Saiu para Entrega', color: 'primary', icon: 'solar:truck-bold' };
    }
    if (trackingStatus === 'available_for_pickup') {
      return { status: 'Aguardando Retirada', color: 'secondary', icon: 'solar:box-bold' };
    }
    if (attentionPriority > 0) {
      return { status: 'Requer Atencao', color: 'warning', icon: 'solar:danger-triangle-bold' };
    }
    if (delivered_at && payment_status !== 'paid') {
      const daysSince = getDaysSinceDelivery(delivered_at);
      if (daysSince !== null && daysSince < 3) {
        return { status: 'Entregue', color: 'success', icon: 'solar:home-2-bold' };
      }
      if (daysSince !== null && daysSince >= 3 && daysSince <= 5) {
        return { status: 'Inadimplente', color: 'danger', icon: 'solar:close-circle-bold' };
      }
      if (daysSince !== null && daysSince > 6) {
        return { status: 'Frustrado', color: 'danger', icon: 'solar:close-circle-bold' };
      }
    }
    if (delivered_at && payment_status === 'paid') {
      return { status: 'Pago', color: 'success', icon: 'solar:check-circle-bold' };
    }

    return { status: 'Em Expedicao', color: 'info', icon: 'solar:box-bold' };
  };

  if (!sale && !loading) return null;

  // Rodape fixo com botoes de acao
  const drawerFooter = sale ? (
    <div className="d-flex flex-wrap gap-2 align-items-center">
      {permissions.canTrack && (
        <Drawer.Button
          variant="primary"
          icon="mdi:truck"
          onClick={() => setShowAddTracking(true)}
        >
          Adicionar Rastreio
        </Drawer.Button>
      )}

      {permissions.canDoAll && (
        <Drawer.Button
          variant="success"
          icon="mdi:cash-check"
          onClick={() => setShowMarkAsPaid(true)}
        >
          Marcar como Pago
        </Drawer.Button>
      )}

      {permissions.canDoAll && (
        <Drawer.Button
          variant="warning"
          icon="mdi:email-send"
          onClick={() => setShowSendEmail(true)}
        >
          Enviar Email
        </Drawer.Button>
      )}

      {permissions.canDoAll && (
        <Drawer.Button
          variant="danger"
          icon="mdi:ticket"
          onClick={() => setShowCreateTicket(true)}
        >
          Criar Ticket
        </Drawer.Button>
      )}

      {!permissions.canDoAll && permissions.canComment && (
        <span className="text-muted small d-flex align-items-center">
          <Icon icon="mdi:information-outline" className="me-1" />
          Voce pode adicionar comentarios
          {permissions.canTrack && ' e rastreio'}
        </span>
      )}
    </div>
  ) : null;

  return (
    <>
      <Drawer
        show={show}
        onHide={onHide}
        title={sale ? `Venda #${sale.id}` : 'Carregando...'}
        icon="solar:file-text-bold"
        size="lg"
        footer={drawerFooter}
      >
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" />
            <p className="mt-2">Carregando detalhes...</p>
          </div>
        ) : sale ? (
          <div style={{ padding: '0 4px' }}>
            {/* Informacoes do Pedido */}
            <Drawer.Section title="Informacoes do Pedido" icon="solar:file-text-bold">
              <div className="row g-3">
                <div className="col-md-6">
                  <Drawer.Row label="ID do Pedido" value={`#${sale.id}`} highlight />
                </div>
                <div className="col-md-6">
                  <Drawer.Row label="Status" value={getPaymentStatusBadge(sale.payment_status)} />
                </div>
                <div className="col-md-6">
                  <Drawer.Row label="Valor Total" value={formatPrice(sale.total_amount)} highlight />
                </div>
                <div className="col-md-6">
                  <Drawer.Row label="Data do Pedido" value={formatDate(sale.created_at)} />
                </div>
                <div className="col-md-6">
                  <Drawer.Row
                    label="Forma de Pagamento"
                    value={
                      <span className="d-flex align-items-center gap-2">
                        <Icon icon={getPaymentMethodIcon(sale.payment_method)} />
                        {getPaymentMethodText(sale.payment_method)}
                      </span>
                    }
                  />
                </div>
                <div className="col-md-6">
                  <Drawer.Row label="Data do Pagamento" value={formatDate(sale.payment_date)} />
                </div>
              </div>

              {sale.product_name && (
                <>
                  <Drawer.Divider />
                  <div
                    className="d-flex align-items-center gap-3 p-3 rounded-3"
                    style={{
                      background: 'linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%)',
                      border: '1px solid #e2e8f0'
                    }}
                  >
                    {sale.product_image ? (
                      <img
                        src={sale.product_image}
                        alt={sale.product_name}
                        style={{
                          width: 64,
                          height: 64,
                          borderRadius: 10,
                          objectFit: 'cover',
                          border: '2px solid #e2e8f0',
                          flexShrink: 0
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: 64,
                          height: 64,
                          borderRadius: 10,
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}
                      >
                        <Icon icon="solar:box-bold" style={{ fontSize: 28, color: '#fff' }} />
                      </div>
                    )}
                    <div className="d-flex flex-column" style={{ minWidth: 0 }}>
                      <span style={{ fontWeight: 600, fontSize: '0.95rem', color: '#1e293b' }}>
                        {sale.product_name}
                      </span>
                      {sale.plan_name && (
                        <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                          Plano: {sale.plan_name}
                        </span>
                      )}
                      {(sale.plan_price || sale.product_price) && (
                        <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#059669', marginTop: 2 }}>
                          {formatPrice(sale.plan_price || sale.product_price)}
                        </span>
                      )}
                    </div>
                  </div>
                </>
              )}

              {sale.is_afterpay && (
                <>
                  <Drawer.Divider />
                  <div className="row g-3">
                    <div className="col-md-6">
                      <Drawer.Row
                        label="Tipo de Pedido"
                        value={
                          <span className="badge bg-danger text-white">
                            <Icon icon="solar:truck-delivery-bold" className="me-1" />
                            AfterPay
                          </span>
                        }
                        highlight
                      />
                    </div>
                    {(() => {
                      const afterPayStatus = getAfterPayStatus();
                      if (afterPayStatus) {
                        return (
                          <div className="col-md-6">
                            <Drawer.Row
                              label="Status AfterPay"
                              value={
                                <span className={`badge bg-${afterPayStatus.color} text-white d-flex align-items-center gap-1`}>
                                  <Icon icon={afterPayStatus.icon} className="text-xs" />
                                  {afterPayStatus.status}
                                </span>
                              }
                              highlight
                            />
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                </>
              )}
            </Drawer.Section>

            <Drawer.Divider />

            {/* Dados do Cliente */}
            <Drawer.Section title="Dados do Cliente" icon="solar:user-bold">
              <Drawer.Row label="Nome" value={sale.customer_name || '-'} />
              {sale.customer_phone && (
                <Drawer.Row label="Telefone" value={sale.customer_phone} />
              )}
              <Drawer.Row label="Email" value={sale.customer_email || '-'} />
              {sale.shipping_address && (() => {
                try {
                  const address = typeof sale.shipping_address === 'string'
                    ? JSON.parse(sale.shipping_address)
                    : sale.shipping_address;

                  const formatted = [
                    `${address.street || ''}${address.number ? `, ${address.number}` : ''}`,
                    address.complement,
                    `${address.neighborhood || ''}${address.city ? `, ${address.city}` : ''}${address.state ? ` - ${address.state}` : ''}`,
                    (address.zipCode || address.zip_code || address.zip) ? `CEP: ${address.zipCode || address.zip_code || address.zip}` : null
                  ].filter(Boolean).join('\n');

                  return (
                    <Drawer.Row
                      label="Endereco"
                      value={
                        <span style={{ whiteSpace: 'pre-line', lineHeight: 1.5 }}>
                          {formatted}
                        </span>
                      }
                    />
                  );
                } catch {
                  return null;
                }
              })()}
            </Drawer.Section>

            {/* Rastreamento */}
            {sale.tracking_code && (
              <>
                <Drawer.Section title="Rastreamento" icon="solar:delivery-bold">
                  <Drawer.Row label="Codigo de Rastreio" value={sale.tracking_code} highlight />
                  {sale.shipping_company && (
                    <Drawer.Row label="Transportadora" value={sale.shipping_company} />
                  )}
                </Drawer.Section>

                <Drawer.Divider />
              </>
            )}

            {/* Extrato Financeiro */}
            <Drawer.Section title="Extrato Financeiro" icon="solar:wallet-money-bold">
              {commissionsLoading ? (
                <div className="text-center py-3">
                  <div className="spinner-border spinner-border-sm text-primary" />
                  <span className="ms-2 text-muted">Carregando...</span>
                </div>
              ) : commissions.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-borderless mb-0" style={{ fontSize: '0.8rem', background: 'transparent', '--bs-table-bg': 'transparent' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                        <th style={{ padding: '6px 8px', fontWeight: 600, color: '#64748b' }}>Tipo</th>
                        <th style={{ padding: '6px 8px', fontWeight: 600, color: '#64748b' }}>Nome</th>
                        <th className="text-end" style={{ padding: '6px 8px', fontWeight: 600, color: '#64748b' }}>Taxa</th>
                        <th className="text-end" style={{ padding: '6px 8px', fontWeight: 600, color: '#64748b' }}>Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {commissions.map((c, index) => (
                        <tr key={index} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '10px 8px' }}>{getRoleTypeBadge(c.role_type)}</td>
                          <td style={{ padding: '10px 8px', fontWeight: 500 }}>
                            {(c.role_type || '').toLowerCase() === 'platform' ? 'Pagmus' : (c.user_name || '-')}
                          </td>
                          <td className="text-end" style={{ padding: '10px 8px', color: '#64748b' }}>
                            {c.calculation_method === 'residual' || (c.role_type || '').toLowerCase() === 'producer'
                              ? '--'
                              : (c.role_type || '').toLowerCase() === 'platform' || c.calculation_method === 'platform'
                                ? `${c.platform_fee_fixed ? formatPrice(c.platform_fee_fixed) + ' + ' : ''}${parseFloat(c.commission_rate || 0).toFixed(2)}%`
                                : c.commission_rate && parseFloat(c.commission_rate) > 0
                                  ? `${parseFloat(c.commission_rate).toFixed(2)}%`
                                  : '-'}
                          </td>
                          <td className="text-end fw-bold" style={{ padding: '10px 8px', color: '#059669' }}>
                            {formatPrice(parseFloat(c.commission_amount || 0) * 100)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ borderTop: '2px solid #e2e8f0' }}>
                        <td colSpan="3" className="fw-bold" style={{ padding: '10px 8px' }}>Total:</td>
                        <td className="text-end fw-bold" style={{ padding: '10px 8px', color: '#059669' }}>
                          {formatPrice(
                            commissions.reduce((sum, c) => sum + parseFloat(c.commission_amount || 0) * 100, 0)
                          )}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <Drawer.EmptyState
                  icon="solar:wallet-money-bold"
                  title="Nenhuma comissao registrada"
                  description="Esta venda ainda nao possui comissoes calculadas."
                />
              )}
            </Drawer.Section>

            <Drawer.Divider />

            {/* Afiliado */}
            {sale.affiliate_name && (
              <>
                <Drawer.Section title="Dados do Afiliado" icon="solar:users-group-rounded-bold">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <Drawer.Row label="Nome" value={sale.affiliate_name} />
                    </div>
                    <div className="col-md-6">
                      <Drawer.Row label="Email" value={sale.affiliate_email || '-'} />
                    </div>
                  </div>
                </Drawer.Section>

                <Drawer.Divider />
              </>
            )}

            {/* Historico de Movimentacao */}
            <Drawer.Section title="Historico de Movimentacao" icon="solar:history-bold">
              <SaleHistoryTimeline
                saleId={saleId}
                history={history}
                loading={historyLoading}
                hasMore={hasMoreHistory}
                onLoadMore={handleLoadMoreHistory}
                onActionSuccess={handleActionSuccess}
                canAddComments={permissions.canComment}
              />
            </Drawer.Section>
          </div>
        ) : null}
      </Drawer>

      {/* Modais de Acao */}
      {sale && (
        <>
          <AddTrackingModal
            show={showAddTracking}
            onHide={() => setShowAddTracking(false)}
            saleId={saleId}
            onSuccess={handleActionSuccess}
          />

          <MarkAsPaidModal
            show={showMarkAsPaid}
            onHide={() => setShowMarkAsPaid(false)}
            saleId={saleId}
            sale={sale}
            onSuccess={handleActionSuccess}
          />

          <SendEmailModal
            show={showSendEmail}
            onHide={() => setShowSendEmail(false)}
            saleId={saleId}
            sale={sale}
            onSuccess={handleActionSuccess}
          />

          <CreateTicketModal
            show={showCreateTicket}
            onHide={() => setShowCreateTicket(false)}
            saleId={saleId}
            sale={sale}
            onSuccess={handleActionSuccess}
          />
        </>
      )}
    </>
  );
};

export default SaleDetailsDrawer;
